import React, { useEffect, useRef, FC } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { MerchantDetail } from '../../../../../interfaces/merchants';
import {
  SchemeType,
  Settings,
} from '../../../../../interfaces/merchants/products/purchases';
import {
  init,
  useWindowPopstate,
} from '../../../../../redux/actions/merchants/products/purchases';
import BaseLayoutMerchantWithShortHeader from '../../../../../components/layouts/BaseLayoutMerchantWithShortHeader';
import SchemeTypeSubscription from '../../../../../organisms/merchants/products/purchases/SchemeTypeSubscription';
import SchemeTypeTicketBook from '../../../../../organisms/merchants/products/purchases/SchemeTypeTicketBook';

import css from './index.module.scss';
import { ReduxState } from '../../../../../redux/reducers';
import { getPurchases } from '../../../../../redux/selectors/merchants/products/purchases';
import { MerchantProductStatus } from '../../../../../interfaces/merchants/products';
import NotAcceptingPurchase from './components/NotAcceptingPurchase';
import { useUserMe } from '../../../../../hooks/api/me';
import { LoadingIconWithReloadButton } from '../../../../../molecules/common/LoadingIconWithReloadButton';
import { useAppDispatch } from '../../../../../redux';
import { captureException } from '@sentry/nextjs';

interface ElementProps {
  publicId: string;
  merchantDetail: MerchantDetail;
  productId: string;
  productStatus: string;
  isCoubicApp: boolean;
}

const MerchantProductPurchasePage: React.FC<ElementProps> = ({
  productId,
  publicId,
  merchantDetail: {
    public_id,
    display_name,
    thumbnail,
    show_profile_image_on_site,
    logo_image,
  },
  productStatus,
  isCoubicApp,
}) => {
  const isRenderedRef = useRef(false);
  const dispatch = useAppDispatch();
  const { isLoggedIn, meResponseData: loginUser } = useUserMe();
  const isEmailVerified = loginUser?.is_email_verified ?? false;

  useWindowPopstate({ dispatch });

  useEffect(() => {
    dispatch(init({ publicId, productId, isEmailVerified }));
  }, [dispatch, publicId, productId, isEmailVerified]);

  const { settings, step } = useSelector(
    (state: ReduxState) => getPurchases(state.merchantsProductsPurchases),
    shallowEqual,
  );

  if (!settings || step === undefined || step === null) {
    return null;
  }

  const isFirstStep = step === 1 || step === 0;
  if (!isRenderedRef.current && !isFirstStep && isLoggedIn) {
    return null;
  }

  isRenderedRef.current = true;

  const merchantProps = {
    publicId: public_id,
    displayName: display_name,
    thumbnailUrl: thumbnail.thumb.url,
    isShowProfileImage: show_profile_image_on_site,
  };

  return (
    <BaseLayoutMerchantWithShortHeader
      className={css.Index}
      merchantProps={merchantProps}
      logoImage={logo_image}
      merchantPublicId={public_id}
      isCoubicApp={isCoubicApp}
    >
      <div className={css.Index__PurchasesArea}>
        <div className={css.Index__PurchasesContent}>
          {productStatus === MerchantProductStatus.Accepting ? (
            <ProductPurchaseFlow
              settings={settings}
              isEmailVerified={isEmailVerified}
              isLoggedIn={isLoggedIn}
            />
          ) : (
            <NotAcceptingPurchase url={`/${publicId}/products/${productId}`} />
          )}
        </div>
      </div>
    </BaseLayoutMerchantWithShortHeader>
  );
};

type ProductPurchaseFlowProps = {
  settings: Settings | null;
  isEmailVerified: boolean;
  isLoggedIn: boolean;
};

const ProductPurchaseFlow: FC<ProductPurchaseFlowProps> = ({
  settings,
  isEmailVerified,
  isLoggedIn,
}) => {
  if (!settings) {
    return <LoadingIconWithReloadButton />;
  }

  if (settings.schemeType === SchemeType.Subscription) {
    return (
      <SchemeTypeSubscription
        isEmailVerified={isEmailVerified}
        isLoggedIn={isLoggedIn}
      />
    );
  }

  if (settings.schemeType === SchemeType.TicketBook) {
    return (
      <SchemeTypeTicketBook
        isEmailVerified={isEmailVerified}
        isLoggedIn={isLoggedIn}
      />
    );
  }

  captureException('Never');
  throw new Error('Never');
};

export default MerchantProductPurchasePage;
